import { EventEmitter, Inject, Injectable } from '@angular/core';
import { GaMessageService } from '../message/ga-message.service';
import { AuthService } from '../services/auth/auth.service';
import { FormattingService } from '../services/formatting.service';
import { BrowseListConfigService, IBrowseListConfig } from '../browselists/browselists-config.service';
import { IExtendedWindow, IObject } from '@type_definitions/datatypes';
import { util } from '../util';
import { IColumnMetaData } from './report-data.service';
import _ from 'underscore';
import {
  _ as agGridUtils,
  ColDef,
  IAggFunc,
  IAggFuncParams,
  ICellRendererFunc,
  ICellRendererParams,
  ProcessCellForExportParams,
  RowClassParams,
  ValueGetterParams,
  RowNode, KeyCreatorParams
} from 'ag-grid-community';
import { IColDef } from './report-grid.service';
import {
  IRichTextGrid,
  IRichTextRendererParams,
  RichTextClickFunction,
  RichTextType
} from '../ag-grid-components/ag-rich-text/ag-rich-text-renderer.component';
import { CellRendererComp } from '../ag-grid-components/ga-ag-grid.component';
import { DEFAULT_DATE_FORMAT } from '../services/date.util';

export type ICellRendererParamsFunc = (params:ICellRendererParams) => any;

export interface ICellStyle {
  cellRenderer:ICellRendererFunc;
  cellRendererParams?:ICellRendererParamsFunc;
  cellClass:string | string[] | ((cellClassParams:any) => string | string[]);
  cellClassRules:any;
  comparator:(valueA:any, valueB:any, nodeA?:RowNode, nodeB?:RowNode, isDescending?:boolean) => number;
  shouldCompile:boolean;
  keyCreator:(value:any) => string;
  valueFormatter:any;
  valueGetter?:((params:ValueGetterParams) => any) | string;
  aggFunc:IAggFunc;
}

interface IReportLink {
  href:string;
  label:string;
}

export interface IReportCellRendererValue {
  value:string;
  properties:IReportCellRendererValueProp;
}

export interface IReportCellRendererValueProp {
  icon?:string;
  iconColor?:string;
  iconVisibility?:string;
  textColor?:string;
  title?:string;

  attachId?:string;
  attachmentId?:string;
  browseListCode?:string;
  currency?:string;
  documentId?:string;
  href?:string;
  invoiceId?:number;
  jiraId?:string;
  linkType?:string;
  orderId?:number;
  ownerType?:string;
  requestId?:number;
  target?:string;
  type?:'PO' | 'PQ';
  template?:string;
  ticketId?:string;
  tab?:string;
  noPopup?:boolean;
  links?:IReportLink[];

  [ key:string ]:any; // allow other keys: value['key']
}

export type ReportDataType = 'DATE' | 'NUMBER' | 'TEXT';
type DataTypes = {
  [index in ReportDataType]:{
    valueFormatter:Function;
    clipboardFormatter:Function;
    comparator?:(value1:IReportCellRendererValue, value2:IReportCellRendererValue) => number;
    keyCreator?(params:KeyCreatorParams):any;
    valueGetter?(params:ValueGetterParams):any;
  }
};

export type ReportScreenType = 'ADDRESS' | 'AMOUNT' | 'DOCUMENT' | 'LINK' | 'PERCENTAGE' | 'POPUP' | 'YEAR' | 'QUARTER' | 'MONTH' | 'DATERANGE';
type ScreenTypes = {
  [index in ReportScreenType]:{
    cellRenderer?:string | Function;
    cellRendererParams?:IObject;
    valueFormatter?:Function;
    clipboardFormatter?:Function;
    comparator?:Function;
    cellClassRules?:{};
    valueGetter?:Function;
  }
};

type ExtraValueFormatterFunc = (params:ICellRendererParams, col?:IColumnMetaData) => string;

const DATE_FORMATS:IObject = {
  'DD/MM/YYY': DEFAULT_DATE_FORMAT,
  'DD/MM/YYYY HH24:MI': 'DD/MM/YYYY HH:mm',
  'DD-MMM-YYYY': 'DD-MMM-YYYY',
  'DD-MON-YYYY': 'DD-MMM-YYYY',
  'DD/MM/YYYY HH:MI': 'DD/MM/YYYY HH:mm',
  'DD/MM/YYYY HH:mm': 'DD/MM/YYYY HH:mm', // this seems useless, but date formatter will use the default if the format is not known
  'DD-MON-YY': 'DD-MMM-YY',
  'YYYY': 'YYYY'
};

export enum ReportIcon {
  EDIT = 'fa-edit',
  ADD = 'fa-plus',
  WARNING = 'fa-exclamation-triangle',
  DANGER = 'fa-exclamation-circle',
  INFO = 'fa-info-circle',
  CHECK = 'fa-check',
  DOWNLOAD = 'fa-download',
  ARCHIVE = 'fa-archive'
}

export enum ReportCellClass {
  PRIMARY = 'text-primary',
  SUCCESS = 'text-success',
  DANGER = 'text-danger',
  WARNING = 'text-warning',
  MUTED = 'text-muted'
}

export enum ReportCellAlign {
  LEFT = 'text-start',
  CENTER = 'text-center',
  RIGHT = 'text-end'
}

export enum ActionStatus {
  ERROR = 'ERROR',
  OK = 'OK'
}

export const REPORT_CELL_PROPERTIES: string[] = ['browseListCode', 'title', 'textColor', 'icon', 'iconColor', 'iconVisibility',
  'noPopup', 'id', 'target', 'linkType', 'href', 'links', 'tab', 'reloadRows'];

@Injectable()
export class ReportStyleService {
  private readonly dataTypes:DataTypes;
  private readonly screenTypes:ScreenTypes;

  constructor(
    private messageService:GaMessageService,
    private authService:AuthService,
    private formattingService:FormattingService,
    private browseListConfigService:BrowseListConfigService,
    @Inject(Window) private window:IExtendedWindow
  ) {
    this.dataTypes = this.listDataTypes();
    this.screenTypes = this.listScreenTypes();
  }

  private listDataTypes():DataTypes {
    const dataType:DataTypes = <any>{};

    dataType.DATE = {
      valueFormatter: this.dateFormatter,
      clipboardFormatter: this.dateFormatter,
      comparator: this.valueComparator,
      keyCreator: this.dateKeyCreator
    };

    dataType.NUMBER = {
      valueFormatter: this.numberFormatter,
      clipboardFormatter: this.numberFormatter,
      comparator: this.valueComparator
    };

    dataType.TEXT = {
      valueFormatter: this.textFormatter,
      clipboardFormatter: this.noopFormatter,
      comparator: this.valueComparator
    };

    return dataType;
  }

  private listScreenTypes():ScreenTypes {
    const screenType:ScreenTypes = <any>{};

    screenType.ADDRESS = {
      cellRenderer: this.addressRenderer,
      clipboardFormatter: this.addressFormatter,
      comparator: this.valueComparator
    };

    screenType.AMOUNT = {
      valueFormatter: this.amountFormatter
    };

    screenType.DOCUMENT = {
      cellRenderer: CellRendererComp.REPORT_DOCUMENT_RENDERER,
      cellRendererParams: {
        getValueFormatted: this.getValueFormatted,
        aggregateRenderer: this.aggregateRenderer
      },
      clipboardFormatter: this.getCellValue,
      comparator: this.valueComparator,
      cellClassRules: {
        'btn-cell': 'x && x.properties && !x.properties.documentId',
        'document-exist': 'x && x.properties && x.properties.documentId',
        'document-not-exist': 'x && x.properties && !x.properties.documentId'
      }
    };

    screenType.LINK = {
      cellRenderer: CellRendererComp.RICH_TEXT_RENDERER,
      cellRendererParams: this.getLinkRendererParams,
      clipboardFormatter: this.getCellValue,
      comparator: this.valueComparator
    };

    screenType.PERCENTAGE = {
      valueFormatter: this.percentageFormatter
    };

    screenType.POPUP = {
      cellRenderer: CellRendererComp.RICH_TEXT_RENDERER,
      cellRendererParams: this.getPopupRendererParams,
      clipboardFormatter: this.getCellValue,
      comparator: this.valueComparator
    };

    screenType.YEAR = {
      valueFormatter: this.noopFormatter,
      clipboardFormatter: this.noopFormatter,
      valueGetter: this.yearValueGetter
    };

    screenType.QUARTER = {
      valueFormatter: this.noopFormatter,
      clipboardFormatter: this.noopFormatter,
      valueGetter: this.quarterValueGetter
    };
    
    screenType.MONTH = {
      valueFormatter: this.noopFormatter,
      clipboardFormatter: this.noopFormatter,
      valueGetter: this.monthValueGetter
    };

    return screenType;
  }

  public getCellStyle(col:IColumnMetaData):ICellStyle {
    const cellStyle:ICellStyle = _.extend({}, this.dataTypes[col.type], this.screenTypes[col.screenType]);
    cellStyle.cellClass = ReportCellAlign[col?.formatting?.align] || undefined;

    if (!cellStyle.keyCreator) {
      cellStyle.keyCreator = (params:ICellRendererParams) => util.extractValue(params?.value);
    }

    if (cellStyle.valueFormatter) {
      cellStyle.valueFormatter = this.wrapValueFormatter(cellStyle.valueFormatter, col);
    }

    if (col.valueGetter) {
      cellStyle.valueGetter = col.valueGetter;
    }

    // when no cellRenderer is defined, here we provide one
    if (!cellStyle.cellRenderer) {
      cellStyle.cellRenderer = this.defaultCellRenderer;
    }

    return cellStyle;
  }

  public rowClassFunction = (params: RowClassParams):string => {
    if (params.data?.actionStatus === ActionStatus.OK) {
      return 'action-ok';
    } else if (params.data?.actionStatus === ActionStatus.ERROR) {
      return 'action-error';
    }
  };

  public cellClipboardFunction = (params:ProcessCellForExportParams) => {
    if (!params.column) {
      return;
    }

    const colDef:IColDef = params.column.getColDef();
    const cellStyle = _.extend({}, this.dataTypes[ colDef.dataType ], this.screenTypes[ colDef.screenType ]);
    if (cellStyle?.clipboardFormatter) {
      return cellStyle.clipboardFormatter(params.value, colDef);
    }

    return this.getValueFormatted(params.value);
  };

  public distinctCountFunction = (params:IAggFuncParams):any => {
    const count = _.chain(params.values)
      .map(util.extractValue)
      .uniq()
      .value().length;
    return count !== 0? count : undefined;
  };

  public countNonBlankFunction = (params:IAggFuncParams):any => {
    const count = _.chain(params.values)
      .map(util.extractValue)
      .filter(v => !!v)
      .value().length;
    return count !== 0? count : undefined;
  };

  // renderer functions
  private defaultCellRenderer = (params:ICellRendererParams):string => {
    return this.wrapValue(this.getValueFormatted(params), params);
  };

  private addressRenderer = (params:ICellRendererParams):string|number => {
    return this.wrapValue(this.addressFormatter(<IReportCellRendererValue>params.value), params);
  };

  public aggregateRenderer = (params:ICellRendererParams):string => {
    return params.valueFormatted || this.formattingService.formatNumber(<string>params.value);
  };

  public headerRenderer(colDef:ColDef):string {
    return colDef.headerName? colDef.headerName.replace('<br>', '\n') : colDef.headerName;
  }

  private getLinkRendererParams = (params:IRichTextRendererParams):IRichTextRendererParams => {
    const value:IReportCellRendererValue = params.value;
    const valueFormatted:string = this.getValueFormatted(params);

    const createSimpleText = (label?:string):IRichTextRendererParams => {
      return <IRichTextRendererParams>{
        type: RichTextType.TEXT,
        getValues: ():IRichTextGrid[] => label ? [{ label }] : []
      };
    };

    if (util.notNullOrUndefinedOrEmpty(value)) {
      const properties:IReportCellRendererValueProp = (<IReportCellRendererValue>value).properties;
      const iconClass:string = properties?.icon? this.getCellIcon(properties.icon, properties.iconColor, false)?.trim() : '';
      const labelClass:string = ReportCellClass[properties?.textColor] || '';
      const title:string|number = properties?.title || valueFormatted || '';
      const target:string = properties?.target || '_blank';
      const headerLabel:string = params?.colDef?.headerName;

      const createLink = (href:string, customTitle?:string, onClickFn?:RichTextClickFunction, wrapperClass?:string):IRichTextRendererParams => {
        return <IRichTextRendererParams> {
          type: RichTextType.LINK,
          getValues: ():IRichTextGrid[] => [
            {
              label: valueFormatted,
              iconClass,
              labelClass,
              wrapperClass: iconClass ? `cell-icon ${wrapperClass || ''}`.trim() : wrapperClass,
              title: customTitle || title,
              target,
              clickFn: onClickFn,
              href
            }
          ]
        };
      };

      if (!properties) {
        return createSimpleText(valueFormatted);
      }

      // if it's not provided a value or an icon -> render it as an empty cell
      if (util.isNullOrUndefinedOrEmpty(valueFormatted) && !iconClass) {
        return createSimpleText();
      }

      switch (properties.linkType) {
        case 'BP':
          if (properties.companyCountryId) {
            return createLink(`#/bpprofiles/cc:${properties.companyCountryId}${properties.tab? `/${properties.tab}` : ''}`);
          } else {
            return createLink(`#/bpprofiles/${properties.bpprofileId}${properties.tab? `/${properties.tab}` : ''}`);
          }

        case 'WEBSHOP':
          return createLink(`#/webshop/${properties.webshopId}${properties.tab? `/management/${properties.tab}` : ''}?menu=WEBSHOP_${properties.webshopId}`);

        case 'ORDER':
          return createLink(`#/orderentry/overview?order=${valueFormatted}`);

        case 'ORDER_EDIT': {
          let link:string = `/unacc/Index.jsp?Demence=GA&URL=O_OrderView_Edit.jsp%3Frow0_Dereqid%3D${properties.orderId}%26ejbEvent%3Dedit`;
          if (properties.tab) {
            link += `%26showTab%3D${properties.tab}`;
          }
          return createLink(link, `Order ${properties.orderId}`);
        }

        case 'QUOTE_EDIT':
          return createLink(`/unacc/Index.jsp?Demence=GA&URL=QuoteView_Edit.jsp%3Frow0_Dereqid%3D${properties.orderId}%26ejbEvent%3Dedit`);

        case 'QUOTE_ORDER_EDIT':
          if (properties.type === 'PQ') {
            return createLink(`/unacc/Index.jsp?Demence=GA&URL=QuoteView_Edit.jsp%3Frow0_Dereqid%3D${properties.requestId}%26ejbEvent%3Dedit`, `Quote ${properties.requestId}`);
          } else if (properties.type === 'PO') {
            return createLink(`/unacc/Index.jsp?Demence=GA&URL=O_OrderView_Edit.jsp%3Frow0_Dereqid%3D${properties.requestId}%26ejbEvent%3Dedit`, `Order ${properties.requestId}`);
          } else {
            return createSimpleText(valueFormatted);
          }

        case 'INVOICE_EDIT': {
          let link:string = '/unacc/Index.jsp?Demence=GA&URL=InvACSLView_Edit.jsp%3FejbEvent%3Dedit';
          if (properties.invoiceId) {
            link += `%26row0_Deinvid%3D${properties.invoiceId}`;
          }
          if (properties.orderId) {
            link += `%26Dereqid%3D${properties.orderId}`;
          }
          if (properties.ownerType) {
            link += `%26Demrgty%3D${properties.ownerType}`;
          }
          if (properties.template) {
            link += `%26wv_Template%3D${properties.template}`;
          }
          link += '%26wv_displayLinked%3D1';

          return createLink(link);
        }

        case 'INVOICE_APPROVE_PROFORMA':
          return createLink(`/unacc/Index.jsp?Demence=GA&URL=InvACSLView_Edit.jsp%3Frow0_Deinvid%3D${properties.invoiceId}%26ejbEvent%3Dedit%26wv_Template%3DInvACSL_Proforma.htm`);

        case 'INVOICE_CONFIRM_PAYMENT':
          return createLink(`/unacc/Index.jsp?Demence=GA&URL=InvACSLView_Edit.jsp%3Frow0_Deinvid%3D${properties.invoiceId}%26ejbEvent%3Dedit%26wv_Template%3DInvACSL_Customer.htm`);

        case 'INVOICE_PRINT':
          return createLink(`/unacc/Index.jsp?Demence=GA&URL=InvACSLView_Print.jsp%3FDereqid%3D%26Deinvid%3D${properties.invoiceId}`);

        case 'ORDER_PRINT':
          return createLink(`/unacc/Index.jsp?Demence=GA&URL=O_OrderView_Print.jsp%3FDereqid%3D${properties.orderId}`);

        case 'ORDER_FEE_INVOICE':
          return createLink(`#/invoice/${properties.invoiceId}/orderfees`);

        case 'TICKET_EDIT':
          return createLink(`/unacc/Index.jsp?Demence=GA&hideTop=1&hideMenu=1&URL=MaintDetView_Edit.jsp%3Frow0_Demndid%3D${properties.ticketId}%26ejbEvent%3Dedit`);

        case 'TICKET_JIRA':
          return createLink(`https://acslworld.atlassian.net/browse/${properties.jiraId}`);

        case 'ATTACH_OPEN':
        case 'ATTACHMENT_OPEN':
        case 'ATTACHMENT_DOWNLOAD':
          return createLink(`/unacc/AttachWriter?row0_Deattid=${properties.attachmentId}`);

        case 'ATTACHMENT_PREVIEW': {
          const onClickFn:RichTextClickFunction = (event:Event):void => {
            event.preventDefault();
            this.onPreviewDocument(headerLabel, params.context.reportId, Number(properties.attachId));
          };

          return createLink('', `Preview ${headerLabel}`, onClickFn);
        }

        case 'VIP':
          if (properties.ticketId) {
            return createLink(`/unacc/MaintDetView_Edit.jsp?row0_Demndid=${properties.ticketId}&ejbEvent=edit`, 'Priority order ticket');
          }
          return createSimpleText();

        case 'WEBSHOP_CHECKLIST':
          return createLink(`#/unacc?menu=WEBSHOP_${properties.webshopId}&url=%2Funacc%2FWP_ReqInfoView_Read.jsp%3Frow0_Desprid%3D${properties.webshopId}%26wv_Template%3DWPN_Check.htm%26wv_Detitle%3DWebshop%2520Checklist`);

        case 'WEBSHOP_LOCAL_INVOICE_SETTINGS':
          return createLink(`#/unacc?menu=WEBSHOP_${properties.webshopId}&url=%2Funacc%2FWP_GeoView_Browse.jsp%3FDesprid%3D${properties.webshopId}%26wv_Template%3DWPN_Geo_InvInfo.htm%26wv_Detitle%3DLocal%2520invoice%2520settings`);

        case 'USER_PROFILE':
          return createLink(`#/userprofile?userId=${properties.userId}`);

        case 'MULTIPLE':
          return <IRichTextRendererParams> {
            type: RichTextType.LINK,
            getValues: ():IRichTextGrid[] => {
              const richTextLinks:IRichTextGrid[] = [];

              _.each(properties.links, (link:IReportLink) => {
                const linkLabel:string = link.label || link.href;
                // only render link that has 'href' properties
                if (link.href) {
                  richTextLinks.push({
                    label: linkLabel,
                    href: link.href,
                    target,
                    title: linkLabel,
                    labelClass: `cell-multiline ${labelClass || ''}`.trim(),
                    iconClass
                  });
                }
              });

              return richTextLinks;
            }
          };

        case 'FAQ_EDIT':
          return createLink(`#/faq/${properties.faqId}/edit`);

        default:
          if (properties.href) {
            const links = util.splitLinks(properties.href);
            if (links.length > 1) { // if there is more than one link in the href, we use multiple links
              const values = util.splitLinks(valueFormatted) || []; // if value contains separators, they need to be split as well

              return <IRichTextRendererParams> {
                type: RichTextType.LINK,
                getValues: ():IRichTextGrid[] => {
                  const richTextLinks:IRichTextGrid[] = [];

                  _.each(links, (href:string, index:number) => {
                    const linkLabel:string = values?.[index] || href;

                    if (href) {
                      richTextLinks.push({
                        label: linkLabel,
                        iconClass,
                        labelClass: `cell-multiline ${labelClass || ''}`.trim(),
                        target,
                        title: linkLabel,
                        href
                      });
                    }
                  });

                  return richTextLinks;
                }
              };
            } else { // or else go to default link render
              return createLink(properties.href, title);
            }
          } else {
            // if link type is not defined and href is null then create simple text
            return createSimpleText(valueFormatted);
          }
        // end switch
      }
    } else {
      return createSimpleText();
    }
  };

  private getPopupRendererParams = (params:IRichTextRendererParams):IRichTextRendererParams => {
    const value:IReportCellRendererValue = (<IReportCellRendererValue>params.value);
    const valueFormatted:string = this.getValueFormatted(params);
    const properties:IReportCellRendererValueProp = (<IReportCellRendererValue>value)?.properties;
    const noIconWanted:boolean = properties?.icon === '';
    // if no icon is defined then fallback to EDIT icon
    const iconClass:string = !noIconWanted && !properties?.icon? ReportIcon.EDIT : this.getCellIcon(properties?.icon, properties?.iconColor, false);
    const labelClass:string = `no-underline ${ReportCellClass[properties?.textColor] || ''}`;
    const title:string|number = properties?.title || valueFormatted || '';

    const createSimpleText = ():IRichTextRendererParams => {
      // render as simple text
      return <IRichTextRendererParams> {
        type: RichTextType.TEXT,
        getValues: ():IRichTextGrid[] => [{ label: valueFormatted }]
      };
    };

    if (value?.properties?.noPopup) {
      // remove icon
      (<IReportCellRendererValue>params.value).properties.icon = '';

      // render as simple text
      return createSimpleText();
    }

    if (value) {
      let config:IBrowseListConfig;
      if (value.properties?.browseListCode) {
        config = this.browseListConfigService.getConfig(value.properties.browseListCode); //can be a real config, or can be undefined
      }
      config = util.copyAndExtend(config || {}, value.properties);
      config.dataBindings = util.copyAndExtend(config.dataBindings || {}, value.properties);
      config.reportId = params?.context?.reportId;

      return <IRichTextRendererParams> {
        type: RichTextType.LINK,
        getValues: ():IRichTextGrid[] => [
          {
            label: valueFormatted,
            href: '',
            title,
            iconClass,
            labelClass,
            wrapperClass: iconClass ? 'cell-icon clickable' : 'clickable',
            clickFn: (event:Event, onBusy:EventEmitter<boolean>) => {
              event.preventDefault();

              if (this.window.editCellFn) {
                onBusy.emit(true);

                this.window.editCellFn(params.node.rowIndex, config)
                  .finally(() => {
                    onBusy.emit(false);
                  });
              } else {
                console.error('Edit function not found');
              }
            }
          }
        ]
      };
    } else {
      return createSimpleText();
    }
  };

  // value getter
  private getCellValue = (value:IReportCellRendererValue):string => {
    return value?.value || '';
  };

  private getValueFormatted = (params:ICellRendererParams):string => {
    if (util.isNullOrUndefinedOrEmpty(params)) {
      return '';
    }

    let valueFormatted:string = params.valueFormatted || util.extractValue(params.value);

    // if stripHtml set in metadata, then we remove html tags within contents
    if ((<IColDef>params.colDef)?.stripHtml) {
      valueFormatted = this.formattingService.removeHtmlTags(util.extractValue(params.value));
    }

    return util.notNullOrUndefined(valueFormatted)?
      (_.isString(valueFormatted)? valueFormatted.replace(/(\s+\n\s*)|(^\s+|\s+$)/g, '\n') : valueFormatted) : '';
  };

  private yearValueGetter = (params:ICellRendererParams):string => {
    const cell = util.extractValue(params.data?.[ params.colDef.field ]);
    return this.formattingService.formatDate(cell, 'YYYY');
  };

  private quarterValueGetter = (params:ICellRendererParams):string => {
    const cell = util.extractValue(params.data?.[ params.colDef.field ]);
    return this.formattingService.formatDate(cell, 'YYYY-Q'); // 2024-1, 2024-2, 2024-3, 2024-4
  };
  
  private monthValueGetter = (params:ICellRendererParams):string => {
    const cell = util.extractValue(params.data?.[params.colDef.field]);
    return this.formattingService.formatDate(cell, 'MMM'); // Jan, Feb, Mar
  };

  // multiple ways of wrapping: 1) original value | 2) original value with a color and title 3) original value with mouse hover | 4) an icon + original value
  private wrapValue(value:string|number, params:ICellRendererParams, title?:string|number, isLink?:boolean, isPopup?:boolean):string {
    value = value === 0? value.toString() : value;
    const properties:IReportCellRendererValueProp = this.getPropertiesValue(params);


    const textColor:string = properties?.textColor;
    let cellTitle:string|number = properties?.title || title || '';

    // display the value
    let cellValue:string = '';

    if (value) {
      const splitValueByLineBreak:string[] = value.toString().trim().split('\n');
      const wrappedText:string = _.map(splitValueByLineBreak, (text:string) => `<span class="cell-multiline">${text}</span>`).join('');
      cellTitle = !isLink && !cellTitle? value : cellTitle;
      cellValue = !isLink && (textColor || cellTitle)? `<span class="report-cell-value ${ReportCellClass[textColor] || ''}" title="${cellTitle}">${_.isString(value) && params.colDef?.autoHeight? wrappedText : value}</span>` : (<string>value);
    }

    if (properties) {
      properties.iconColor = properties.iconColor || textColor; // use the text color for the icon if no icon color is set
      const noIconWanted:boolean = properties.icon === '';
      const icon:string = (isPopup && !noIconWanted && !properties.icon)? 'EDIT' : properties.icon; // when the icon is empty use a default icon named 'Edit'

      // display the icon
      if (icon || noIconWanted) {
        if (properties.iconVisibility === 'always' || value || isPopup) { // show always the icon or only when the cell has a value
          return `<div class="cell-icon${isPopup? ' clickable' : ''}" title="${cellTitle}">${!isLink? this.getCellIcon(icon, properties.iconColor) : ''}${cellValue}</div>`;
        }
      }
    }

    return cellValue;
  }

  // value formatter
  private dateFormatter = (value:IReportCellRendererValue|string, metadata:IColumnMetaData):string => {
    let format:string = metadata?.formatting?.format;
    format = format && DATE_FORMATS[ format ]? DATE_FORMATS[ format ] : DEFAULT_DATE_FORMAT;
    return this.formattingService.formatDate(util.extractValue(value), format);
  };

  private numberFormatter = (value:IReportCellRendererValue|string, col:IColumnMetaData):string|number => {
    let number:string = util.extractValue(value);
    if (util.notNullOrUndefinedOrEmpty(number) && col?.formatting?.format) {
      number = this.formattingService.formatNumber(number, col.formatting.format);
    }

    return util.isNullOrUndefinedOrEmpty(number)? '' : number;
  };

  private textFormatter = (value:IReportCellRendererValue|string):string => {
    return _.escape(util.extractValue(value));
  };

  private addressFormatter = (value:IReportCellRendererValue):string|number => {
    if (value?.properties) {
      const loc:IReportCellRendererValueProp = value.properties;
      return this.formattingService.join(
        loc.name,
        loc.address,
        this.formattingService.join(loc.zip, loc.city, ' '),
        loc.state,
        loc.country,
        ', '
      );
    }
    return '';
  };

  private amountFormatter = (value:IReportCellRendererValue | string, col:IColumnMetaData):string => {
    let amount:string = `${this.numberFormatter(value, col)}`;
    if ((<IReportCellRendererValue>value)?.properties?.currency && util.notNullOrUndefinedOrEmpty(amount)) {
      amount += ` ${(<IReportCellRendererValue>value).properties.currency}`;
    }
    return amount;
  };

  private percentageFormatter = (value:IReportCellRendererValue | string, col:IColumnMetaData):string => {
    return util.notNullOrUndefinedOrEmpty(value) && `${this.numberFormatter(value, col)} %`;
  };

  private wrapValueFormatter = (valueFormatter:ExtraValueFormatterFunc, col:IColumnMetaData):Function => {
    return (params:ICellRendererParams) => {
      return valueFormatter(params.value, col); // call to function specified in dateFormatter (listDataTypes and listScreenTypes)
    };
  };

  private noopFormatter = (value:IReportCellRendererValue|string):string => {
    return util.extractValue(value);
  };

  // helper function
  private onPreviewDocument(label:string, reportId:number, documentId:number):boolean {
    if (!documentId) {
      this.messageService.showErrorMessage('No document found');
    } else {
      const url = this.authService.authenticatedUrl(`rest/reports/${reportId}/documents/${documentId}?inline=true`);
      this.messageService.showGA1Dialog(`Preview ${label || ''}`, url);
    }
    return false;
  }

  private getCellIcon(icon:string, iconColor?:string, withElement:boolean = true):string {
    let iconClass:string = ReportIcon[icon] || '';
    // set icon color
    if (iconColor) {
      iconClass += ` ${ReportCellClass[iconColor] || ''}`;
    }
    return withElement ? `<i class="fa ${iconClass}"></i>` : iconClass;
  }

  // comparator
  private valueComparator = (value1:IReportCellRendererValue, value2:IReportCellRendererValue):number => {
    // value1.value/value2.value can be an object but having value null
    const v1:IReportCellRendererValue|string = util.extractValue(value1);
    const v2:IReportCellRendererValue|string = util.extractValue(value2);
    return agGridUtils.defaultComparator(v1, v2);
  };

  // key creator
  public dateKeyCreator = (params:KeyCreatorParams):string => {
    const value:string = util.extractValue(params.value);
    // make the dateKeyCreator empty when grouping
    return value ? this.formattingService.formatDate(value, 'YYYY-MM') : ' ';
  };

  // get properties value from row data
  private getPropertiesValue(params:ICellRendererParams):IObject {
    return (<IReportCellRendererValue>params?.value)?.properties || params.data?.[params.colDef?.field]?.properties;
  }
}